import { Alert, Box, Button, Divider, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH } from './authConstants';
import { loginWithEmail, renderGoogleLoginButton } from './LoginUtils';

/**
 * @param {Object} props
 * @param {Function} props.onGoogleLoginSuccess
 * @param {Function} props.onEmailLoginSuccess - called with user profile after successful email/password login
 */
export default function LoginScreen({ onGoogleLoginSuccess, onEmailLoginSuccess }) {
    const [showInactivityMessage, setShowInactivityMessage] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailError, setEmailError] = useState(null);
    const [emailSubmitting, setEmailSubmitting] = useState(false);

    useEffect(() => {
        renderGoogleLoginButton('googleLogin', onGoogleLoginSuccess);

        const inactivityLogout = sessionStorage.getItem('inactivityLogout');
        if (inactivityLogout === 'true') {
            setShowInactivityMessage(true);
            sessionStorage.removeItem('inactivityLogout');
        }
    }, [onGoogleLoginSuccess]);

    async function handleEmailSubmit(e) {
        e.preventDefault();
        setEmailError(null);
        setEmailSubmitting(true);
        try {
            await loginWithEmail({ email, password }, onEmailLoginSuccess);
        } catch (err) {
            setEmailError(err.message);
        } finally {
            setEmailSubmitting(false);
        }
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                background: 'var(--background-color)',
                color: 'var(--text-primary)',
            }}
        >
            {showInactivityMessage && (
                <Alert severity="info" sx={{ mb: 3, maxWidth: '500px', width: '100%' }}>
                    Session Expired. For your security, you have been logged out due to inactivity.
                </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '440px', px: 2 }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 1.5, textAlign: 'center' }}>
                    Sign in to your secure workspace
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: 'var(--text-secondary)', mb: 3, textAlign: 'center', lineHeight: 1.6 }}
                >
                    Please sign in to ensure secure access to case documents
                </Typography>

                <Box
                    component="form"
                    onSubmit={handleEmailSubmit}
                    sx={{ width: '100%', maxWidth: '340px', display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}
                >
                    {emailError && <Alert severity="error">{emailError}</Alert>}
                    <TextField
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        fullWidth
                        size="small"
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={emailSubmitting}
                        fullWidth
                        sx={{
                            background: 'var(--button-gradient)',
                            color: 'white',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '8px',
                            py: 1.2,
                            boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
                            '&:hover': {
                                background: 'var(--button-gradient-hover)',
                                boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
                            },
                            '&:disabled': {
                                background: 'var(--text-secondary)',
                                opacity: 0.5,
                                color: 'white',
                            },
                        }}
                    >
                        {emailSubmitting ? 'Signing in…' : 'Sign in with email'}
                    </Button>
                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                        <Link to="/forgot-password">Forgot password?</Link>
                        {' · '}
                        <Link to="/register">Create account</Link>
                    </Typography>
                    <Typography variant="caption" sx={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                        Password must be at least {PASSWORD_MIN_LENGTH} characters
                    </Typography>
                </Box>

                <Divider sx={{ width: '100%', maxWidth: '340px', my: 2 }}>or</Divider>

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '340px',
                        borderRadius: '14px',
                        padding: '20px 24px',
                        background: 'linear-gradient(145deg, rgba(244, 208, 63, 0.12) 0%, rgba(212, 175, 55, 0.06) 100%)',
                        border: '2px solid var(--accent-color)',
                    }}
                >
                    <div id="googleLogin" />
                </Box>

                <Typography
                    component="div"
                    sx={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', mt: 3 }}
                >
                    By signing in, you agree to our{' '}
                    <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
                </Typography>
            </Box>
        </div>
    );
}
